import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Paper,
    Stack,
    Chip,
    TextField,
    CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    LocationOn as LocationIcon,
    Work as WorkIcon,
    AttachMoney as MoneyIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { jobVacancyService } from '../services/jobVacancyService';
import { JobVacancy, SENIORITY_LABELS } from '../types/jobVacancy';

export const PublicVacancies: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [vacancies, setVacancies] = useState<JobVacancy[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadVacancies();
    }, []);

    const loadVacancies = async () => {
        try {
            setLoading(true);
            const data = await jobVacancyService.getPublicVacancies();
            setVacancies(data);
        } catch (error) {
            enqueueSnackbar('Erro ao carregar vagas', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredVacancies = vacancies.filter((vacancy) => {
        if (!searchTerm) return true;
        
        const searchTermLower = searchTerm.toLowerCase();
        const role = vacancy.role || '';
        const seniority = vacancy.seniority || '';
        const location = vacancy.location || '';
        const workModel = vacancy.work_model || '';

        return (
            role.toLowerCase().includes(searchTermLower) ||
            seniority.toLowerCase().includes(searchTermLower) ||
            location.toLowerCase().includes(searchTermLower) ||
            workModel.toLowerCase().includes(searchTermLower)
        );
    });

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #F4F5F7 0%, #ffffff 100%)',
            pt: { xs: 4, md: 8 },
            pb: 12
        }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
                    <Box component="img" src="/logo.svg" alt="Qaracter"
                        sx={{ height: { xs: 32, md: 40 }, mb: 3, bgcolor: '#374754', p: 1.5, borderRadius: 2 }} />
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                            overflowWrap: 'anywhere',
                            color: '#374754',
                            '& span': { color: '#F87936' }
                        }}
                    >
                        Vagas Disponíveis
                    </Typography>
                    <Typography 
                        variant="h5" 
                        color="text.secondary" 
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            mb: { xs: 4, md: 6 },
                            fontSize: { xs: '1.1rem', md: '1.5rem' }
                        }}
                    >
                        Encontre a oportunidade perfeita para sua carreira. Temos vagas em diferentes áreas e níveis de experiência.
                    </Typography>

                    {/* Search Bar */}
                    <Paper 
                        elevation={2}
                        sx={{ 
                            p: 2, 
                            maxWidth: 600, 
                            mx: 'auto',
                            display: 'flex', 
                            alignItems: 'center',
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <SearchIcon sx={{ color: 'text.secondary', mr: 2 }} />
                        <TextField
                            fullWidth
                            placeholder="Buscar vagas por cargo, senioridade, localização..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                            }}
                        />
                    </Paper>
                </Box>

                {/* Vacancies Grid */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredVacancies.length === 0 ? (
                    <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
                        {vacancies.length === 0
                            ? 'Nenhuma vaga aberta no momento. Volte em breve!'
                            : 'Nenhuma vaga encontrada para essa busca.'}
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {filteredVacancies.map((vacancy) => (
                            <Grid item xs={12} md={6} key={vacancy.id}>
                                <Card sx={{ 
                                    height: '100%', 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                                    }
                                }}>
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                            <Box>
                                                <Typography variant="h5" gutterBottom component="h2" sx={{ fontWeight: 600 }}>
                                                    {vacancy.role}
                                                </Typography>
                                                <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 500 }}>
                                                    {vacancy.seniority && (SENIORITY_LABELS[vacancy.seniority] || vacancy.seniority)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Stack spacing={2}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {vacancy.location && (
                                                    <Chip
                                                        icon={<LocationIcon />}
                                                        label={vacancy.location}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {vacancy.work_model && (
                                                    <Chip
                                                        icon={<WorkIcon />}
                                                        label={
                                                            vacancy.work_model === 'REMOTE' ? 'Remoto' :
                                                            vacancy.work_model === 'HYBRID' ? 'Híbrido' : 'Presencial'
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {vacancy.contract_type && (
                                                    <Chip
                                                        icon={<MoneyIcon />}
                                                        label={vacancy.contract_type}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>

                                            <Typography variant="body2" color="text.secondary" sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                mb: 2
                                            }}>
                                                {vacancy.description}
                                            </Typography>

                                            {vacancy.benefits && (
                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Benefícios:
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {vacancy.benefits}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </CardContent>
                                    <CardActions sx={{ p: 3, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => navigate(`/vagas/publicas/${vacancy.id}`)}
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{ py: 1.5 }}
                                        >
                                            Ver detalhes e candidatar-se
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>
        </Box>
    );
}; 